import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Locality, Province } from '../../models/address';
import {
  AuthorizationRequest,
  AuthorizationResponse,
  Benefit,
  BenefitGroup,
  HealthMedicalCenter,
  HealthMedicalCenterDetail,
  HealthPrescriberDocument,
  SpecialityInfo
} from '../../models/authorization';
import { Bill } from '../../models/bill';
import {
  MailMessage,
  MailParticularData,
  SendMessageRequest
} from '../../models/mail';
import { Base64File } from '../../models/payment-plan';
import {
  Document,
  Policy,
  PolicyCopaymentCard,
  PolicyCopaymentCorresponse,
  PolicyMedicalAssistance,
  PolicyMedicalAuthorization,
  PolicyReceipt
} from '../../models/policy';
import {
  getFullName,
  MedicalGuide,
  PolicyHealth,
  PolicyHealthInsured,
  Refund
} from '../../models/policy-health';
import { PendingReceipt } from '../../models/receipt';
import { RefundRequest, RefundResponse } from '../../models/refund-request';
import { AuthMirrorService } from '../core/auth/auth-mirror.service';
import { BaseService } from '../core/base.service';
import { LogService } from '../core/log.service';
import { MMobileHelperService } from '../core/mmobile-helper.service';
import { OperationRegisterService } from '../core/operation-register.service';
import { AddressHelperService } from '../helpers/address-helper.service';
import { BaseMailService } from './base-mail.service';

@Injectable({
  providedIn: 'root'
})
export class HealthPoliciesService {
  private specialityInfo: SpecialityInfo[];

  constructor(
    private http: HttpClient,
    private logService: LogService,
    private mmobileHelper: MMobileHelperService,
    private base: BaseService,
    private baseMailService: BaseMailService,
    private authMirror: AuthMirrorService,
    private addressHelper: AddressHelperService,
    private or: OperationRegisterService
  ) {}

  getHealthPolicy({
    policyNumber,
    figure
  }: PolicyHealth): Observable<PolicyHealth> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}?figure=${figure}`;
    return this.http
      .get<PolicyHealth>(
        url,
        this.base.getPrivateOIMHeaders({ cacheResponse: true })
      )
      .pipe(map(p => this.completePolicyData(p)));
  }

  getHealthReceipts(policy: PolicyHealth): Observable<PolicyReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${
      policy.policyNumber
    }/receipts?companyId=${policy.companyId}`;
    return this.http.get<PolicyReceipt[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params: this.or.setAssistanceParams(policy)
    });
  }

  getHealthReceipt(
    policy: PolicyHealth,
    receiptNumber: string
  ): Observable<PolicyReceipt> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${
      policy.policyNumber
    }/receipts/${receiptNumber}?companyId=${policy.companyId}`;
    return this.http.get<PolicyReceipt>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params: this.or.setAssistanceParams(policy)
    });
  }

  getHealthReceiptDocument(
    policyNumber: string,
    companyId: string,
    receiptNumber: string,
    params: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/documents/receipts/${receiptNumber}?companyId=${companyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getHealthPolicyCard(policyNumber: string) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/card`;
    return this.http.get<MedicalGuide>(url, this.base.getPrivateOIMHeaders());
  }

  getHealthPendingReceipts(
    policyNumber: string,
    companyId: string,
    params: any
  ): Observable<PendingReceipt[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/receiptsPayment/receipts?companyId=${companyId}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<PendingReceipt[]>(url, { ...headers, params });
  }

  getHealthMedicalAssistances(
    policy: PolicyHealth,
    insured: PolicyHealthInsured
  ): Observable<PolicyMedicalAssistance[]> {
    const { policyNumber } = policy;
    const { cis } = insured;
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/assistances`;
    return this.http.get<PolicyMedicalAssistance[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params: this.or.setAssistanceParams(policy)
    });
  }

  getHealthMedicalAuthorizations(
    policy: PolicyHealth,
    cis: string
  ): Observable<PolicyMedicalAuthorization[]> {
    const { policyNumber } = policy;
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/authorizations`;
    return this.http.get<PolicyMedicalAuthorization[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params: this.or.setAssistanceParams(policy)
    });
  }

  getHealthParticularConditions(
    policyNumber: string,
    productCode: string,
    params?: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/documents/particularConditions?productCode=${productCode}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  getHealthCardAddress(policyNumber: string): Observable<string> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/documents/cardDuplicate/address`;
    return this.http.get<string>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getHealthGeneralConditions(
    policyNumber: string,
    productCode: string,
    generalConditionsCode: string,
    params: HttpParams
  ): Observable<Document> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/documents/generalConditions?product=${productCode}&generalConditionsCode=${generalConditionsCode}`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Document>(url, { ...headers, params });
  }

  duplicateHealthCard(
    insured: PolicyHealthInsured,
    cause: string,
    address: string
  ) {
    const { user } = this.authMirror.authService.getState();

    const data: MailParticularData[] = [];
    data.push({ field: 'Motivo', value: cause });
    data.push({ field: 'Nombre', value: getFullName(insured) });
    data.push({ field: 'NIF', value: insured.documentNumber });
    data.push({ field: 'cisAsegurado', value: insured.cis });
    data.push({ field: 'Documento', value: 'Nueva Tarjeta Sanitaria' });
    data.push({ field: 'direccion', value: address });
    data.push({ field: 'fecha nacimiento', value: insured.birthdate });

    const msg: MailMessage = {
      sendingFile: false,
      read: true,
      mailBoxId: '3',
      inboxId: '1',
      responseModeId: '2',
      type: '0',
      frmId: '447',
      groupId: '68',
      from: user.email ? user.email : null
    };
    const request: SendMessageRequest = {
      mailboxIdentifier: user.documentNumber,
      particularData: data,
      message: msg
    };
    return this.baseMailService.sendMessage(request);
  }

  getAuthBenefits(
    policyNumber: string,
    cis: string
  ): Observable<BenefitGroup[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/authorizations/benefits`;
    return this.http.get<BenefitGroup[]>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  isValidateDocuments(
    policyNumber: string,
    cis: string,
    prescriptionDocumentNumber: string
  ): Observable<HealthPrescriberDocument> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/authorizations/prescriptions/${prescriptionDocumentNumber}`;
    return this.http.get<HealthPrescriberDocument>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getMedicalCenterDetailByPrescriptor(
    policyNumber: string,
    cis: string,
    prescriberCode: number
  ): Observable<HealthMedicalCenterDetail> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/authorizations/medicalCenters/${prescriberCode}`;
    return this.http.get<HealthMedicalCenterDetail>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getMedicalCenterProvinces(
    policyNumber: string,
    cis: string
  ): Observable<Province[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/authorizations/provinces`;
    return this.http.get<Province[]>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getMedicalCenterLocality(
    policyNumber: string,
    cis: string,
    provinceCode: number
  ): Observable<Locality> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/authorizations/provinces/${provinceCode}/localities`;
    return this.http.get<Locality>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getMedicalCenterList(
    policyNumber: string,
    cis: string,
    prov: Province,
    loc: Locality,
    bene: Benefit,
    pCode: number,
    center: string,
    type: boolean,
    size: number = 50
  ): Promise<any> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/authorizations/medicalCenters/search?size=${size}`;
    const body = {
      province: prov,
      locality: loc,
      postalCode: pCode,
      centerName: center,
      benefit: bene,
      hospitalization: type
    };
    return forkJoin([
      this.getSpecialities(),
      this.http.post<HealthMedicalCenter[]>(
        url,
        body,
        this.base.getPrivateOIMHeaders()
      )
    ])
      .pipe(map(res => res[1].map(l => this.changeSpecialty(res[0], l))))
      .toPromise();
  }

  getMedicalCenterDetail(
    policyNumber: string,
    cis: string,
    serviceNumber: number
  ): Observable<HealthMedicalCenterDetail> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/authorizations/medicalCenters/${serviceNumber}`;
    return this.http.get<HealthMedicalCenterDetail>(
      url,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  validateAuthorization(
    policyNumber: string,
    cis: string,
    authorization: AuthorizationRequest
  ): Observable<AuthorizationResponse> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/authorizations/validate`;
    return this.http.post<AuthorizationResponse>(
      url,
      authorization,
      this.base.getPrivateOIMHeaders({ cacheResponse: true })
    );
  }

  getHealthRenewalNotice(policy: Policy): Observable<Document> {
    const { policyNumber: healthPolicyNumber } = policy || {};
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${healthPolicyNumber}/documents/renewalNotice`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const params = this.or.calculateDocumentROParams(policy);
    return this.http.get<Document>(
      url,
      { ...headers, params }
    );
  }

  getHealthRefundList(
    policy: PolicyHealth,
    insured: PolicyHealthInsured
  ): Observable<Refund[]> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${
      policy.policyNumber
    }/insureds/${insured.cis}/refunds`;
    return this.http.get<Refund[]>(url, {
      ...this.base.getPrivateOIMHeaders({ cacheResponse: true }),
      params: this.or.setAssistanceParams(policy)
    });
  }

  async existRefundBill(
    policy: Policy,
    insured: PolicyHealthInsured,
    bill: Bill
  ) {
    // tslint:disable-next-line:max-line-length
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${
      policy.policyNumber
    }/insureds/${insured.cis}/refunds/bills?date=${
      bill.date
    }&doctorOrPharmacyDocumentNumber=${
      bill.doctorOrPharmacyDocumentNumber
    }&amount=${bill.amount}`;
    const headers = this.base.getPrivateOIMHeaders();
    return this.http.get(url, headers).toPromise();
  }

  async requestRefund(
    policy: Policy,
    insured: PolicyHealthInsured,
    request: RefundRequest
  ): Promise<any> {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${
      policy.policyNumber
    }/insureds/${insured.cis}/refunds`;
    const headers = this.base.getPrivateOIMHeaders();
    return this.http.post<RefundResponse>(url, request, headers).toPromise();
  }

  private completePolicyData(policy: PolicyHealth): PolicyHealth {
    const medicalRefundMenu: string[] = this.mmobileHelper.getCustomConfigData(
      'medicalRefundMenu'
    );
    const refundOverrideProductCodes: string[] = this.mmobileHelper.getCustomConfigData(
      'refundOverrideProductCodes'
    );
    // MedicalExpenses
    if (
      medicalRefundMenu &&
      policy.menu &&
      medicalRefundMenu.indexOf(policy.menu) !== -1
    ) {
      policy.insureds.forEach(i => {
        i.medicalExpensesRefundEnabled = true;
      });
    } else {
      policy.insureds.forEach(i => {
        i.medicalExpensesRefundEnabled = false;
      });
    }
    // OverridePharmaceutical
    if (
      refundOverrideProductCodes &&
      policy.productCode &&
      refundOverrideProductCodes.indexOf(policy.productCode) !== -1
    ) {
      policy.insureds.forEach(i => {
        i.pharmaceuticalExpensesMinimumRequired = true;
        i.pharmaceuticalExpensesRefundEnabled = true;
      });
    }
    return policy;
  }

  private getSpecialities(): Observable<any[]> {
    if (this.specialityInfo) {
      return of(this.specialityInfo);
    }
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/specialities?size=100`;
    return this.http
      .get<SpecialityInfo[]>(url, this.base.getPrivateOIMHeaders())
      .pipe(
        catchError(err => {
          this.logService.e(
            this,
            `Error loading authorizations specialities. ${JSON.stringify(err)}`
          );
          return of(null);
        }),
        tap(s => (this.specialityInfo = s)) // do(s => this.specialityInfo = s);
      );
  }

  private changeSpecialty(
    specialties: SpecialityInfo[],
    center: HealthMedicalCenter
  ): any {
    if (specialties == null || center == null) {
      return center;
    }
    const specialty = specialties.find(
      s => +s.code === center.serviceSpecialityCode
    );
    center.serviceSpecialityDescription = specialty ? specialty.name : null;
    return center;
  }

  getCopaymentCardList(
    policyNumber: string,
    cis: string,
    status: boolean,
    documentNumber: string
  ) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/copay/documents`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    const structure = { documentNumber };
    const params = this.or.setCopaymentCardParams(
      status,
      JSON.stringify(structure)
    );
    return this.http.get<PolicyCopaymentCard[]>(url, { ...headers, params });
  }

  getCopaymentCardById(
    policyNumber: string,
    cis: string,
    copaymentCard: PolicyCopaymentCard,
    params?: HttpParams
  ) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}/copay/documents/${
      copaymentCard.id
    }`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<Base64File>(url, { ...headers, params });
  }

  getCopaymentCardStatus(policyNumber: string, cis: string) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${cis}//copay/correspondance`;
    const headers = this.base.getPrivateOIMHeaders({ cacheResponse: true });
    return this.http.get<PolicyCopaymentCorresponse>(url, headers);
  }

  setCopaymentStatus(
    policyNumber: string,
    insured: PolicyHealthInsured,
    status: boolean
  ) {
    const url = `${this.base.getPrivateOIMBaseUrl()}/healthPolicies/${policyNumber}/insureds/${
      insured.cis
    }/copay/correspondance`;
    const headers = this.base.getPrivateOIMHeaders();
    const body = { checked: status };
    const structure = {
      documentNumber: insured.documentNumber,
      'carta en papel': !status ? 'SI' : 'NO'
    };
    const params = this.or.setCopaymentCardParams(
      status,
      JSON.stringify(structure)
    );
    return this.http.put(url, body, { ...headers, params });
  }
}
